import { combineSeed } from '../city/cityLayout.js';
import { Random } from '../../utils/Random.js';
import {
  buildCeiling,
  buildFloor,
  buildPerimeterWalls,
} from './interiorShell.js';
import {
  addPropBox,
  addPropPillar,
  addSign,
  addWallProp,
} from './interiorDesignHelpers.js';

/** @typedef {import('./interiorShell.js').InteriorBuildContext} InteriorBuildContext */

/**
 * @typedef {{
 *   id: number;
 *   name: string;
 *   width: number;
 *   depth: number;
 *   height: number;
 *   spawnLocal: { x: number; z: number };
 *   build: (ctx: InteriorBuildContext) => void;
 * }} InteriorDesignDef
 */

function designTerminalLobby(ctx) {
  const { w, d, h, accentMaterial, buildingMaterial } = ctx;
  addSign(ctx, {
    x: 0,
    y: h * 0.62,
    z: -d / 2 + 0.5,
    phrases: ['ACCESS GRANTED', 'SIMULATION ACTIVE'],
    seedOffset: 50,
  });
  addPropBox(ctx, {
    material: accentMaterial,
    size: [2.2, 1.0, 0.55],
    position: [0, 0.58, -d / 2 + 1.0],
    cellsPerMeter: 7,
    seedOffset: 51,
    name: 'Terminal',
  });
  addPropPillar(ctx, {
    material: accentMaterial,
    size: [0.35, h * 0.88, 0.35],
    position: [-w * 0.32, h * 0.44, 0.5],
    seedOffset: 52,
    name: 'PillarL',
  });
  addPropPillar(ctx, {
    material: accentMaterial,
    size: [0.35, h * 0.88, 0.35],
    position: [w * 0.32, h * 0.44, 0.5],
    seedOffset: 53,
    name: 'PillarR',
  });
  addPropBox(ctx, {
    material: accentMaterial,
    size: [w * 0.55, 0.08, 0.2],
    position: [0, h - 0.35, -d * 0.15],
    cellsPerMeter: 8,
    seedOffset: 54,
    name: 'LightStrip',
    collider: false,
  });
}

function designServerRoom(ctx) {
  const { w, d, h, accentMaterial, buildingMaterial } = ctx;
  addSign(ctx, {
    x: 0,
    y: h * 0.7,
    z: -d / 2 + 0.45,
    width: 2.6,
    phrases: ['NODE CLUSTER', '010101010101'],
    seedOffset: 60,
  });
  const rackH = Math.min(2.35, h * 0.72);
  for (let row = 0; row < 3; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      const x = -w * 0.36 + col * (w * 0.24);
      const z = -d * 0.28 + row * (d * 0.22);
      addPropBox(ctx, {
        material: accentMaterial,
        size: [0.85, rackH, 0.55],
        position: [x, rackH / 2 + 0.06, z],
        cellsPerMeter: 8,
        seedOffset: 61 + row * 4 + col,
        name: `Rack_${row}_${col}`,
      });
    }
  }
  addPropBox(ctx, {
    material: buildingMaterial,
    size: [w - 1.2, 0.05, d - 1.2],
    position: [0, 0.14, 0],
    cellsPerMeter: 5,
    seedOffset: 80,
    name: 'RaisedFloor',
    collider: false,
  });
}

function designTransitCorridor(ctx) {
  const { w, d, h, accentMaterial, buildingMaterial } = ctx;
  addWallProp(ctx, {
    material: buildingMaterial,
    size: [w * 0.55, h * 0.92, 0.28],
    position: [0, h * 0.46, 0],
    seedOffset: 70,
    name: 'CenterPartition',
  });
  addSign(ctx, {
    x: -w * 0.22,
    y: h * 0.55,
    z: -d * 0.35,
    rotY: Math.PI / 2,
    phrases: ['CONNECTION LOST', 'SIGNAL DETECTED'],
    seedOffset: 71,
  });
  addSign(ctx, {
    x: w * 0.22,
    y: h * 0.55,
    z: d * 0.2,
    rotY: -Math.PI / 2,
    phrases: ['SYSTEM ONLINE', 'ACCESS DENIED'],
    seedOffset: 72,
  });
  addPropBox(ctx, {
    material: accentMaterial,
    size: [1.4, 0.45, 0.4],
    position: [-w * 0.35, 0.28, d * 0.32],
    seedOffset: 73,
    name: 'BenchA',
  });
  addPropBox(ctx, {
    material: accentMaterial,
    size: [1.4, 0.45, 0.4],
    position: [w * 0.35, 0.28, -d * 0.32],
    seedOffset: 74,
    name: 'BenchB',
  });
}

function designArchiveVault(ctx) {
  const { w, d, h, buildingMaterial, accentMaterial } = ctx;
  addSign(ctx, {
    x: 0,
    y: h * 0.65,
    z: -d / 2 + 0.42,
    phrases: ['ARCHIVE VAULT', 'RECORDS SEALED'],
    seedOffset: 90,
  });
  for (let i = 0; i < 3; i += 1) {
    const z = -d * 0.25 + i * (d * 0.22);
    addPropBox(ctx, {
      material: buildingMaterial,
      shape: 'facade',
      size: [0.35, h * 0.75, 2.4],
      position: [-w / 2 + 0.55, h * 0.38, z],
      cellsPerMeter: 7,
      seedOffset: 91 + i,
      name: `ShelfL_${i}`,
    });
    addPropBox(ctx, {
      material: buildingMaterial,
      shape: 'facade',
      size: [0.35, h * 0.75, 2.4],
      position: [w / 2 - 0.55, h * 0.38, z],
      cellsPerMeter: 7,
      seedOffset: 94 + i,
      name: `ShelfR_${i}`,
    });
  }
  addPropBox(ctx, {
    material: accentMaterial,
    size: [2.6, 0.75, 1.1],
    position: [0, 0.42, 0.2],
    seedOffset: 100,
    name: 'ReadingDesk',
  });
  addPropPillar(ctx, {
    material: accentMaterial,
    size: [0.22, h * 0.5, 0.22],
    position: [1.1, h * 0.25, 0.2],
    seedOffset: 101,
    name: 'Lamp',
    collider: false,
  });
}

function designRedChapel(ctx) {
  const { w, d, h, accentMaterial, buildingMaterial } = ctx;
  addSign(ctx, {
    x: 0,
    y: h * 0.78,
    z: -d / 2 + 0.5,
    width: 2.5,
    height: 0.7,
    phrases: ['WAKE UP', 'THE MATRIX HAS YOU'],
    seedOffset: 110,
  });
  const positions = [
    [-w * 0.35, -d * 0.35],
    [w * 0.35, -d * 0.35],
    [-w * 0.35, d * 0.15],
    [w * 0.35, d * 0.15],
  ];
  positions.forEach(([x, z], i) => {
    addPropPillar(ctx, {
      material: accentMaterial,
      size: [0.42, h * 0.95, 0.42],
      position: [x, h * 0.48, z],
      seedOffset: 111 + i,
      name: `ChapelPillar_${i}`,
    });
  });
  addPropBox(ctx, {
    material: buildingMaterial,
    size: [w * 0.42, 0.22, d * 0.28],
    position: [0, 0.17, -d * 0.22],
    seedOffset: 120,
    name: 'Platform',
  });
  addPropBox(ctx, {
    material: accentMaterial,
    size: [1.4, 0.95, 0.7],
    position: [0, 0.72, -d * 0.22],
    seedOffset: 121,
    name: 'Altar',
  });
}

function designOpsFloor(ctx) {
  const { w, d, h, accentMaterial, buildingMaterial } = ctx;
  addSign(ctx, {
    x: 0,
    y: h * 0.68,
    z: -d / 2 + 0.48,
    phrases: ['OPS FLOOR', 'MONITOR ALL CHANNELS'],
    seedOffset: 130,
  });
  addPropBox(ctx, {
    material: buildingMaterial,
    size: [w * 0.55, 0.85, d * 0.38],
    position: [0, 0.48, 0],
    seedOffset: 131,
    name: 'OpsTable',
  });
  const corners = [
    [-w * 0.38, -d * 0.32],
    [w * 0.38, -d * 0.32],
    [-w * 0.38, d * 0.28],
    [w * 0.38, d * 0.28],
  ];
  corners.forEach(([x, z], i) => {
    addPropBox(ctx, {
      material: accentMaterial,
      shape: 'facade',
      size: [0.9, 1.1, 0.12],
      position: [x, 0.62, z],
      cellsPerMeter: 8,
      seedOffset: 132 + i,
      name: `Monitor_${i}`,
      collider: false,
    });
    addPropBox(ctx, {
      material: buildingMaterial,
      size: [0.75, 1.35, 0.55],
      position: [x, 0.72, z - 0.35],
      seedOffset: 140 + i,
      name: `Cabinet_${i}`,
    });
  });
}

function designResidentialPod(ctx) {
  const { w, d, h, buildingMaterial, accentMaterial } = ctx;
  addWallProp(ctx, {
    material: buildingMaterial,
    size: [0.22, h * 0.85, d * 0.45],
    position: [0.15, h * 0.43, -d * 0.12],
    seedOffset: 150,
    name: 'RoomDivider',
  });
  addSign(ctx, {
    x: -w * 0.28,
    y: h * 0.58,
    z: -d / 2 + 0.45,
    rotY: Math.PI / 2,
    phrases: ['HOME NODE', 'REST CYCLE'],
    seedOffset: 151,
  });
  addPropBox(ctx, {
    material: buildingMaterial,
    size: [w * 0.42, 0.35, 1.6],
    position: [-w * 0.22, 0.24, d * 0.22],
    seedOffset: 152,
    name: 'Bed',
  });
  addPropBox(ctx, {
    material: accentMaterial,
    size: [1.2, 0.95, 0.65],
    position: [w * 0.22, 0.52, -d * 0.28],
    seedOffset: 153,
    name: 'Kitchen',
  });
  addPropBox(ctx, {
    material: buildingMaterial,
    shape: 'wall',
    size: [w * 0.35, h * 0.35, 0.08],
    position: [w * 0.22, h * 0.42, -d / 2 + 0.55],
    seedOffset: 154,
    name: 'Window',
    collider: false,
  });
}

function designWaitingHall(ctx) {
  const { w, d, h, accentMaterial, buildingMaterial } = ctx;
  addSign(ctx, {
    x: 0,
    y: h * 0.62,
    z: -d / 2 + 0.45,
    phrases: ['PLEASE WAIT', 'QUEUE PROCESSING'],
    seedOffset: 160,
  });
  const benches = [
    [-w * 0.32, 0],
    [w * 0.32, 0],
    [0, -d * 0.28],
  ];
  benches.forEach(([x, z], i) => {
    addPropBox(ctx, {
      material: accentMaterial,
      size: [2.2, 0.42, 0.55],
      position: [x, 0.27, z],
      seedOffset: 161 + i,
      name: `Bench_${i}`,
    });
  });
  addPropBox(ctx, {
    material: accentMaterial,
    size: [1.1, 1.65, 0.75],
    position: [0, 0.88, d * 0.22],
    seedOffset: 170,
    name: 'Kiosk',
  });
  addPropBox(ctx, {
    material: buildingMaterial,
    size: [w * 0.7, 0.06, 0.35],
    position: [0, h - 0.42, -d * 0.1],
    seedOffset: 171,
    name: 'Canopy',
    collider: false,
  });
}

function designResearchLab(ctx) {
  const { w, d, h, accentMaterial, buildingMaterial } = ctx;
  addSign(ctx, {
    x: 0,
    y: h * 0.72,
    z: -d / 2 + 0.48,
    phrases: ['EXPERIMENT ACTIVE', 'BIO DIGITAL LAB'],
    seedOffset: 180,
  });
  const tanks = [
    [-w * 0.3, -d * 0.25],
    [w * 0.3, -d * 0.25],
    [-w * 0.3, d * 0.2],
    [w * 0.3, d * 0.2],
  ];
  tanks.forEach(([x, z], i) => {
    addPropPillar(ctx, {
      material: accentMaterial,
      size: [0.75, h * 0.62, 0.75],
      position: [x, h * 0.31, z],
      cellsPerMeter: 9,
      seedOffset: 181 + i,
      name: `Tank_${i}`,
    });
  });
  addPropBox(ctx, {
    material: accentMaterial,
    size: [2.4, 0.9, 1.0],
    position: [0, 0.52, 0],
    seedOffset: 190,
    name: 'LabConsole',
  });
  addPropBox(ctx, {
    material: accentMaterial,
    size: [w * 0.65, 0.04, 0.45],
    position: [0, 0.16, d * 0.35],
    cellsPerMeter: 9,
    seedOffset: 191,
    name: 'HazardStrip',
    collider: false,
  });
}

function designGardenSimulation(ctx) {
  const { w, d, h, buildingMaterial, accentMaterial } = ctx;
  addSign(ctx, {
    x: 0,
    y: h * 0.7,
    z: -d / 2 + 0.5,
    phrases: ['GARDEN SIM', 'NATURE SUBROUTINE'],
    seedOffset: 200,
  });
  addPropBox(ctx, {
    material: buildingMaterial,
    shape: 'facade',
    size: [w - 0.8, 0.12, d - 0.8],
    position: [0, h - 0.18, 0],
    cellsPerMeter: 4,
    seedOffset: 201,
    name: 'SkyPanel',
    collider: false,
  });
  addPropPillar(ctx, {
    material: accentMaterial,
    size: [0.55, 1.15, 0.55],
    position: [0, 0.62, 0.05],
    seedOffset: 202,
    name: 'Fountain',
  });
  const planters = [
    [-w * 0.38, -d * 0.38],
    [w * 0.38, -d * 0.38],
    [-w * 0.38, d * 0.38],
    [w * 0.38, d * 0.38],
  ];
  planters.forEach(([x, z], i) => {
    addPropBox(ctx, {
      material: buildingMaterial,
      size: [1.5, 0.55, 1.1],
      position: [x, 0.32, z],
      seedOffset: 203 + i,
      name: `Planter_${i}`,
    });
    addPropPillar(ctx, {
      material: accentMaterial,
      size: [0.2, 0.45, 0.2],
      position: [x, 0.78, z],
      seedOffset: 210 + i,
      name: `Plant_${i}`,
      collider: false,
    });
  });
}

/** @type {InteriorDesignDef[]} */
export const INTERIOR_DESIGNS = [
  {
    id: 0,
    name: 'Terminal Lobby',
    width: 10,
    depth: 8,
    height: 3.2,
    spawnLocal: { x: 0, z: -2.2 },
    build: designTerminalLobby,
  },
  {
    id: 1,
    name: 'Server Room',
    width: 12,
    depth: 10,
    height: 3.5,
    spawnLocal: { x: 0, z: -3.2 },
    build: designServerRoom,
  },
  {
    id: 2,
    name: 'Transit Corridor',
    width: 9,
    depth: 14,
    height: 3.1,
    spawnLocal: { x: 0, z: -4.5 },
    build: designTransitCorridor,
  },
  {
    id: 3,
    name: 'Archive Vault',
    width: 11,
    depth: 9,
    height: 3.4,
    spawnLocal: { x: 0, z: -2.5 },
    build: designArchiveVault,
  },
  {
    id: 4,
    name: 'Red Chapel',
    width: 10,
    depth: 10,
    height: 4.0,
    spawnLocal: { x: 0, z: -2.8 },
    build: designRedChapel,
  },
  {
    id: 5,
    name: 'Ops Floor',
    width: 14,
    depth: 10,
    height: 3.6,
    spawnLocal: { x: 0, z: -3.0 },
    build: designOpsFloor,
  },
  {
    id: 6,
    name: 'Residential Pod',
    width: 8,
    depth: 9,
    height: 3.0,
    spawnLocal: { x: -1.2, z: -2.0 },
    build: designResidentialPod,
  },
  {
    id: 7,
    name: 'Waiting Hall',
    width: 12,
    depth: 8,
    height: 3.25,
    spawnLocal: { x: 0, z: -2.4 },
    build: designWaitingHall,
  },
  {
    id: 8,
    name: 'Research Lab',
    width: 10,
    depth: 11,
    height: 3.5,
    spawnLocal: { x: 0, z: -3.0 },
    build: designResearchLab,
  },
  {
    id: 9,
    name: 'Garden Simulation',
    width: 11,
    depth: 11,
    height: 3.8,
    spawnLocal: { x: 0, z: -2.6 },
    build: designGardenSimulation,
  },
];

export const INTERIOR_DESIGN_COUNT = INTERIOR_DESIGNS.length;

/**
 * Stable per-building seed (must use full combineSeed arity — never `combineSeed(seed, salt)` alone).
 * @param {number} citySeed
 * @param {number} gx
 * @param {number} gz
 * @param {number} buildingSeed
 * @param {number} slot
 */
export function deriveInteriorSeed(citySeed, gx, gz, buildingSeed, slot) {
  return combineSeed(
    combineSeed(buildingSeed, gx, gz, 8181),
    citySeed,
    slot,
    27017,
  );
}

/**
 * @param {number} interiorSeed
 */
export function pickInteriorDesignIndex(interiorSeed) {
  const rng = new Random(interiorSeed >>> 0);
  return rng.int(0, INTERIOR_DESIGN_COUNT - 1);
}

/**
 * @param {number} designIndex
 */
export function getInteriorDesign(designIndex) {
  const n = INTERIOR_DESIGN_COUNT;
  const i = ((designIndex % n) + n) % n;
  return INTERIOR_DESIGNS[i];
}
